import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import { SiteLayout } from '@/components/SiteLayout';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { CATEGORIES, TOTAL_PROBLEMS } from '@/data/problems';
import { progressKey, useProgress } from '@/hooks/useProgress';
import { cn } from '@/lib/utils';

export default function ProgressPage() {
  useSeoMeta({ title: 'Your progress — JSBat', description: 'Track which JavaScript problems you have solved.' });
  const { progress, reset } = useProgress();
  const solved = Object.values(progress).filter((p) => p.solved).length;
  const pct = Math.round((solved / TOTAL_PROBLEMS) * 100);

  return (
    <SiteLayout>
      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl">Your progress</h1>
            <p className="mt-3 text-lg text-muted-foreground">
              {solved} of {TOTAL_PROBLEMS} problems solved ({pct}%). Saved in this browser.
            </p>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline">Reset progress</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Reset all progress?</AlertDialogTitle>
                <AlertDialogDescription>This clears every solved mark and all your saved code. It can&apos;t be undone.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={reset}>Reset</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        <div className="mt-6 h-3 overflow-hidden rounded-full bg-muted" aria-hidden>
          <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${pct}%` }} />
        </div>

        <div className="mt-10 space-y-8">
          {CATEGORIES.map((c) => (
            <section key={c.id}>
              <h2 className="mb-3 font-mono text-lg font-bold">
                <Link to={`/problems/${c.id}`} className="hover:text-brand-strong">{c.name}</Link>
              </h2>
              <div className="flex flex-wrap gap-2">
                {c.problems.map((p) => {
                  const s = progress[progressKey(c.id, p.id)];
                  return (
                    <Link
                      key={p.id}
                      to={`/problems/${c.id}/${p.id}`}
                      className={cn(
                        'rounded-md border px-3 py-1.5 font-mono text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        s?.solved
                          ? 'border-success/40 bg-success/15 text-success hover:bg-success/25'
                          : s?.attempted
                            ? 'border-amber-500/40 bg-amber-500/10 text-amber-700 hover:bg-amber-500/20 dark:text-amber-300'
                            : 'bg-card hover:bg-accent',
                      )}
                    >
                      {p.fn}
                    </Link>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </div>
    </SiteLayout>
  );
}
